/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Link } from '@vkontakte/vkui';
import { ReactComponent as ConnectErrorIcon } from 'img/icons/uploader/connectError.svg';
import React from 'react';
import { GLOBAL_FILE_SIZE_LIMIT, IS_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import { abHeavyFileBuyConfig, abHeavyFileBuyConfigV3, abHeavyFileBuyV3 } from 'reactApp/appHelpers/featuresHelpers/features/heavyFileBuy';
import { getUploadBigFileBaseTariff } from 'reactApp/components/UploadBigFileDialog/hooks/useTariff';
import { EShowTariffType } from 'reactApp/components/UploadBigFileDialog/UploadBigFileDialog.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { UPLOAD_PROMO_LINK } from 'reactApp/modules/features/features.helpers';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { type IInputFile, EFileError } from 'reactApp/modules/uploadList/uploadList.model';
import { store as reduxStore } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';
import { formatPrice } from 'reactApp/utils/priceHelpers';

import { ProductsSelectors } from '../products/products.selectors';

export const getErrorInfo = (
    error: EFileError,
    {
        file,
        userFileSizeLimit,
        fileName,
        folderName,
    }: { file: IInputFile; userFileSizeLimit?: string; fileName?: string; folderName?: string }
) => {
    const linkText = 'Подробнее о загрузке файлов';

    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (error) {
        case EFileError.LOCAL_FILE_NOT_FOUND:
            return {
                text: 'Файл больше недоступен по указанному пути в локальной файловой системе. Возможно вы извлекли съемный диск, переименовали или удалили файл',
                pureText:
                    'Файл больше недоступен по указанному пути в локальной файловой системе. Возможно вы извлекли съемный диск, переименовали или удалили файл',
            };
        case EFileError.NAME_TOO_LONG:
            return {
                text: 'Превышено количество символов в названии файла',
                pureText: 'Превышено количество символов в названии файла',
                link: {
                    text: linkText,
                    url: 'https://help.mail.ru/cloud_web/confines',
                },
            };
        case EFileError.IGNORED_FILE:
            return {
                text: (
                    <>
                        Служебный файл <span>{fileName}</span> операционной системы игнорируется при загрузке в Облако
                    </>
                ),
                pureText: `Служебный файл ${fileName} операционной системы игнорируется при загрузке в Облако`,
                link: {
                    text: linkText,
                    url: 'https://help.mail.ru/cloud_web/files/upload',
                },
            };
        case EFileError.SKIPPED_FILE:
            return {
                text: 'Файл был пропущен',
                pureText: 'Файл был пропущен',
            };
        case EFileError.FILE_EXIST:
            return {
                text: (
                    <>
                        Файл <span>{fileName}</span> уже существует в вашем Облаке
                    </>
                ),
                pureText: `Файл ${fileName} уже существует в вашем Облаке`,
            };
        case EFileError.OVER_QUOTA_LIMIT_DOWNLOAD:
        case EFileError.CANCELLED_FILE:
            return {
                text: 'Загрузка файла была отменена',
                pureText: 'Загрузка файла была отменена',
            };
        case EFileError.CONNECTION_ERROR:
            return {
                text: 'Нет интернета',
                pureText: 'Нет интернета',
                description: 'Мы сохранили текущий результат. Загрузка продолжится, когда интернет снова появится',
                icon: <ConnectErrorIcon />,
                mode: 'connectError',
                link: {
                    text: 'Подробнее об умной загрузке',
                    url: UPLOAD_PROMO_LINK,
                },
            };
        case EFileError.DLP:
            return {
                text: 'Загрузка остановлена',
                pureText: 'Загрузка остановлена',
                description: 'Объект не прошел проверку. Обратитесь в службу информационной безопасности',
                mode: 'connectError',
            };
        case EFileError.READ_ONLY_DIRECTORY:
            return {
                text: 'Папка доступна только для чтения',
                pureText: 'Папка доступна только для чтения',
                link: {
                    text: linkText,
                    url: 'https://help.mail.ru/cloud_web/folders/public',
                },
            };
        case EFileError.INVALID_CHARACTERS:
            return {
                text: 'Недопустимое имя файла или папки',
                pureText: 'Недопустимое имя файла или папки',
                link: {
                    text: linkText,
                    url: 'https://help.mail.ru/cloud_web/confines',
                },
            };
        case EFileError.USER_FILE_SIZE_LIMIT:
            return {
                text: `Текущий тарифный план не позволяет загружать файлы больше ${userFileSizeLimit}`,
                pureText: `Текущий тарифный план не позволяет загружать файлы больше ${userFileSizeLimit}`,
                link: {
                    text: 'Увеличить лимит',
                    url: 'https://help.mail.ru/cloud_web/size/increase',
                },
            };
        case EFileError.OVER_QUOTA_CLOUD_AT_OWNER:
            return {
                text: `Загрузка невозможна. У владельца этой папки недостаточно места в Облаке для ваших файлов`,
                pureText: `Загрузка невозможна. У владельца этой папки недостаточно места в Облаке для ваших файлов`,
            };
        case EFileError.GLOBAL_FILE_SIZE_LIMIT_ERROR: {
            const limitView = GLOBAL_FILE_SIZE_LIMIT.view;
            return {
                text: `В Облако нельзя загружать файлы размером более ${limitView}`,
                pureText: `В Облако нельзя загружать файлы размером более ${limitView}`,
            };
        }
        case EFileError.INVALID_CHARACTERS_AUTO_FIX:
            return {
                text: (
                    <>
                        Название файла <span>{fileName}</span> было автоматически исправлено. В Облаке действуют ограничения на спецсимволы
                    </>
                ),
                pureText: `Название файла ${fileName} было автоматически исправлено. В Облаке действуют ограничения на спецсимволы`,
                link: {
                    text: linkText,
                    url: 'https://help.mail.ru/cloud_web/about/confines#name',
                },
            };
        case EFileError.NAME_TOO_LONG_AUTO_FIX:
            return {
                text: (
                    <>
                        Название файла <span>{fileName}</span> было автоматически сокращено. В Облаке действует ограничение на длину
                    </>
                ),
                pureText: `Название файла ${fileName} было автоматически сокращено. В Облаке действует ограничение на длину`,
                ...(!IS_BIZ_USER && {
                    link: {
                        text: linkText,
                        url: 'https://help.mail.ru/cloud_web/about/confines',
                    },
                }),
            };
        case EFileError.OVER_QUOTA_CLOUD_B2B:
            return {
                text: (
                    <>
                        <div>Недостаточно свободного места в вашем Облаке.</div>
                        <div>Свяжитесь с&nbsp;администратором вашего Облака для изменения лимитов.</div>
                    </>
                ),
                pureText: `Недостаточно свободного места в вашем Облаке.\n Свяжитесь с администратором вашего Облака для изменения лимитов.`,
            };
        case EFileError.LOCAL_FILE_READ_ERROR:
            return {
                text: 'Ошибка при чтении файла',
                pureText: 'Ошибка при чтении файла',
            };
        case EFileError.VIOLATED_FILENAME:
            return {
                text: (
                    <>
                        <strong>Файл запрещенного формата</strong>
                        <br />
                        Файл <span>{folderName ? `${fileName} из ${folderName.split('/')[1]}` : fileName}</span> не может быть загружен.
                        <br />
                        Обратитесь к администратору
                    </>
                ),
                pureText: `Файл ${fileName} не может быть загружен.\n Обратитесь к администратору`,
            };
        case EFileError.FILE_TOO_BIG:
            return {
                text: 'Загрузка невозможна. У владельца этой папки недостаточно места в Облаке для ваших файлов',
                pureText: 'Загрузка невозможна. У владельца этой папки недостаточно места в Облаке для ваших файлов',
            };
        /* tempexp_18869-start tempexp_18955-start */
        case EFileError.HEAVY_FILE_BUY: {
            let product: Product | undefined;
            let price = 0;
            const productId = (abHeavyFileBuyV3 ? abHeavyFileBuyConfigV3 : abHeavyFileBuyConfig).usual;
            if (productId === 'auto') {
                const { showTariffType, trial, usualProduct } = getUploadBigFileBaseTariff(file.size || 0);
                product = showTariffType === EShowTariffType.TRIAL ? trial : usualProduct;
                price = (showTariffType === EShowTariffType.TRIAL ? product?.discountPrice : product?.price) || 0;
            } else {
                product = ProductsSelectors.getProductById(reduxStore.getState(), productId);
                price = product?.price || 0;
            }
            return {
                text: `Файл весит больше ${userFileSizeLimit}`,
                pureText: `Файл весит больше ${userFileSizeLimit}`,
                description: (
                    <>
                        Такие файлы можно загружать с подпиской
                        <br />
                        Mail Space —{' '}
                        <Link
                            onClick={() => {
                                sendPaymentGa({ action: 'heavy-file', label: 'click' });
                                reduxStore.dispatch(
                                    openTariffBuy({
                                        productId: product?.id,
                                        paySource: 'cloud_popup_heavy_file',
                                        isMobile: EnvironmentSelectors.isMobile(),
                                    })
                                );
                            }}
                        >
                            попробуйте за {formatPrice(price)} ₽
                        </Link>
                    </>
                ),
                link: { onClick: () => {} },
            };
        }
        /* tempexp_18869-end tempexp_18955-end */
        case EFileError.UNKNOWN:
        default:
            return {
                text: 'Неизвестная ошибка',
                pureText: 'Неизвестная ошибка',
            };
    }
};
